// Address privacy helpers.
// Firestore cannot hide fields inside a document, so street addresses, full
// postcodes, and the customer's name/photo are stored on
// jobs/{id}/private/details. The public job doc only gets a coarse area.

// BEGIN toCoarseArea
export function toCoarseArea(value) {
  if (value == null) return '';
  const compact = String(value).trim().replace(/\s+/g, ' ');
  if (!compact) return '';
  const upper = compact.toUpperCase();

  // Full UK postcode anywhere in the string → outward code only (SA1 1AB → SA1).
  const embedded = upper.match(/([A-Z]{1,2}\d[A-Z\d]?)\s*\d[A-Z]{2}/);
  if (embedded) return embedded[1];

  // Already just the outward code / postcode district.
  const outward = upper.match(/^([A-Z]{1,2}\d[A-Z\d]?)$/);
  if (outward) return outward[1];

  // Town or area name, with no house number.
  if (/^[A-Za-z][A-Za-z .'-]{0,40}$/.test(compact)) return compact;

  return '';
}
// END toCoarseArea

// Fields that must never be written to the public jobs/{id} document.
export const SENSITIVE_JOB_KEYS = [
  'pickupAddress',
  'dropoffAddress',
  'customerName',
  'customerPhoto',
  'pickup',
  'dropoff',
  'location',
  'email',
  'phone',
  'phoneNumber',
  'customerEmail',
  'customerPhone',
];

const PRIVATE_DETAIL_KEYS = [
  'pickup',
  'dropoff',
  'pickupAddress',
  'dropoffAddress',
  'customerName',
  'customerPhoto',
];

export function splitJobForStorage(jobData = {}) {
  const pickup = jobData.pickup || jobData.location || '';
  const dropoff = jobData.dropoff || '';
  const privateFields = {
    pickup: pickup || '',
    dropoff: dropoff || '',
    pickupAddress: jobData.pickupAddress || '',
    dropoffAddress: jobData.dropoffAddress || '',
    customerName: jobData.customerName || '',
    customerPhoto: jobData.customerPhoto || '',
  };

  const publicFields = {};
  Object.entries(jobData).forEach(([key, value]) => {
    if (SENSITIVE_JOB_KEYS.includes(key)) return;
    if (value === undefined) return;
    publicFields[key] = value;
  });

  publicFields.pickupArea = toCoarseArea(pickup) || toCoarseArea(jobData.pickupAddress) || '';
  const dropoffArea = toCoarseArea(dropoff) || toCoarseArea(jobData.dropoffAddress);
  if (dropoffArea) publicFields.dropoffArea = dropoffArea;

  return { publicFields, privateFields };
}

// Prefer a non-empty private value. Empty strings must not wipe a legacy copy
// of the same field that is still on the public doc before migration.
export function mergeJobPrivate(publicJob, privateDetails) {
  if (!publicJob) return null;
  if (!privateDetails) return publicJob;
  const merged = { ...publicJob };
  PRIVATE_DETAIL_KEYS.forEach((key) => {
    const value = privateDetails[key];
    if (value == null) return;
    if (typeof value === 'string' && value.trim() === '') return;
    merged[key] = value;
  });
  return merged;
}

// What an open-jobs card may show. Never the street or the full postcode.
export function jobAreaLabel(job) {
  if (!job) return '';
  return (
    toCoarseArea(job.pickupArea) ||
    toCoarseArea(job.pickup) ||
    toCoarseArea(job.location) ||
    toCoarseArea(job.pickupAddress) ||
    ''
  );
}

// Real waste carrier registration, or '' when the value is missing or the old 'Pending' placeholder.
export function licenceForPaperwork(licenceNo) {
  const value = String(licenceNo ?? '').trim();
  if (!value || /^pending$/i.test(value)) return '';
  return value;
}
